import { WebContainer, auth } from '@webcontainer/api';
import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';

// User Provided Client ID
const CLIENT_ID = 'wc_api_mendar_f54ac7a83b93e336b0a09cfcc38b4d72';

// Initialize auth ONCE at module level
let authInitialized = false;
if (!authInitialized) {
    auth.init({
        clientId: CLIENT_ID,
        scope: '',
    });
    authInitialized = true;
}

// Singleton instance
let webContainerInstance: WebContainer | null = null;
let bootPromise: Promise<WebContainer> | null = null;

// Build result type
interface BuildResult {
    success: boolean;
    error?: string;
}

// Deployed file type
interface DeployFile {
    name: string;
    content: string;
}

interface WebContainerContextType {
    webContainer: WebContainer | null;
    isLoading: boolean;
    error: string | null;
    isReady: boolean;
    buildProject: () => Promise<BuildResult>;
    extractDistFiles: () => Promise<DeployFile[]>;
}

const WebContainerContext = createContext<WebContainerContextType>({
    webContainer: null,
    isLoading: true,
    error: null,
    isReady: false,
    buildProject: async () => ({ success: false, error: 'Not initialized' }),
    extractDistFiles: async () => [],
});

export function WebContainerProvider({ children }: { children: ReactNode }) {
    const [container, setContainer] = useState<WebContainer | null>(webContainerInstance);
    const [isLoading, setIsLoading] = useState(!webContainerInstance);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function boot() {
            // If already booted, just return
            if (webContainerInstance) {
                setContainer(webContainerInstance);
                setIsLoading(false);
                return;
            }

            // If currently booting, wait for it
            if (bootPromise) {
                try {
                    const instance = await bootPromise;
                    setContainer(instance);
                    setIsLoading(false);
                } catch (err: any) {
                    setError(err.message);
                    setIsLoading(false);
                }
                return;
            }

            // Start booting
            try {
                console.log('[WebContainer] Pre-booting at app startup...');

                bootPromise = WebContainer.boot();
                const instance = await bootPromise;

                webContainerInstance = instance;
                setContainer(instance);
                console.log('[WebContainer] Pre-boot complete! Ready to use.');

            } catch (err: any) {
                console.error('[WebContainer] Pre-boot failed:', err);
                setError(err.message || 'Failed to boot WebContainer');
                bootPromise = null;
            } finally {
                setIsLoading(false);
            }
        }

        boot();
    }, []);

    // Build the project using npx vite build
    const buildProject = useCallback(async (): Promise<BuildResult> => {
        if (!container) {
            return { success: false, error: 'WebContainer not ready' };
        }

        try {
            console.log('[WebContainer] Starting build with npx vite build...');

            // Use npx to run vite build - this ensures vite is found in node_modules/.bin
            const buildProcess = await container.spawn('npx', ['vite', 'build']);

            let output = '';

            // Start collecting output (don't await - it's async)
            buildProcess.output.pipeTo(new WritableStream({
                write(data) {
                    output += data;
                    console.log('[Build]', data);
                }
            })).catch(() => { }); // Ignore stream errors

            // Wait for process to exit with timeout
            const exitCode = await Promise.race([
                buildProcess.exit,
                new Promise<number>((_, reject) =>
                    setTimeout(() => reject(new Error('Build timeout after 60s')), 60000)
                )
            ]);

            // Give a moment for output to flush
            await new Promise(r => setTimeout(r, 500));

            if (exitCode === 0) {
                console.log('[WebContainer] Build completed successfully');

                // Verify dist folder exists
                try {
                    const distEntries = await container.fs.readdir('/dist');
                    console.log('[WebContainer] dist/ contains:', distEntries);
                    if (distEntries.length === 0) {
                        return { success: false, error: 'Build succeeded but dist/ is empty' };
                    }
                } catch (dirErr) {
                    console.error('[WebContainer] dist/ folder not found after build');
                    return { success: false, error: 'dist/ folder not created. Build may have failed silently.' };
                }

                return { success: true };
            } else {
                console.error('[WebContainer] Build failed with exit code:', exitCode);
                return { success: false, error: `Build failed (exit code ${exitCode}): ${output.slice(-500)}` };
            }
        } catch (err: any) {
            console.error('[WebContainer] Build error:', err);
            return { success: false, error: err.message || 'Build failed' };
        }
    }, [container]);

    // Recursively extract all files from dist/ folder
    const extractDistFiles = useCallback(async (): Promise<DeployFile[]> => {
        if (!container) {
            console.error('[WebContainer] Cannot extract files - container not ready');
            return [];
        }

        const files: DeployFile[] = [];

        async function readDir(path: string, prefix: string = '') {
            try {
                const entries = await container!.fs.readdir(path, { withFileTypes: true });

                for (const entry of entries) {
                    const fullPath = `${path}/${entry.name}`;
                    const relativePath = prefix ? `${prefix}/${entry.name}` : entry.name;

                    if (entry.isDirectory()) {
                        await readDir(fullPath, relativePath);
                    } else if (entry.isFile()) {
                        try {
                            const content = await container!.fs.readFile(fullPath, 'utf-8');
                            files.push({
                                name: relativePath,
                                content: content as string
                            });
                        } catch (readErr) {
                            // For binary files, read as base64
                            try {
                                const binaryContent = await container!.fs.readFile(fullPath);
                                const uint8 = new Uint8Array(binaryContent as unknown as ArrayBufferLike);
                                const base64 = btoa(String.fromCharCode(...uint8));
                                files.push({
                                    name: relativePath,
                                    content: base64
                                });
                            } catch (binaryErr) {
                                console.warn(`[WebContainer] Could not read file: ${fullPath}`, binaryErr);
                            }
                        }
                    }
                }
            } catch (err) {
                console.error(`[WebContainer] Error reading directory ${path}:`, err);
            }
        }

        console.log('[WebContainer] Extracting files from dist/...');
        await readDir('/dist');
        console.log(`[WebContainer] Extracted ${files.length} files from dist/`);

        return files;
    }, [container]);

    return (
        <WebContainerContext.Provider value={{
            webContainer: container,
            isLoading,
            error,
            isReady: !!container && !isLoading,
            buildProject,
            extractDistFiles,
        }}>
            {children}
        </WebContainerContext.Provider>
    );
}

export function useWebContainer() {
    return useContext(WebContainerContext);
}

// Export for backwards compatibility
export { webContainerInstance };

